/**
 * The panel a selected place opens: how good it is, and what is published
 * about it.
 *
 * Two kinds of number sit here and the difference matters, so the panel keeps
 * them apart. The **blended score** moves with the sliders, because it is the
 * reader's own question asked of this place. The **four category scores** do
 * not: they are this place's percentile within London in each category, which
 * is a fact about the place and not about what the reader asked for. Under
 * *more detail*, one line per category is what the publisher said -- ONS's
 * median price, the Met's offence count, MHCLG's decile, the nearest station
 * and the closest school -- and each carries an `i` that says whose index it is
 * and links the page defining it.
 *
 * A row is also a way into its category's layer, so the panel and the cards on
 * the left are two handles on the one control: tapping *Transport* here does
 * exactly what tapping the Transport card does, and tapping it again comes back
 * to the blend.
 *
 * This file only renders. Everything it prints is computed by the caller, so
 * there is one place that reads the rasters and one place that draws them.
 */

import { escapeHtml, percent } from "./format.ts";
import { linkHost } from "./sources.ts";
import type { SourceNote } from "./sources.ts";

export interface PanelRow {
  name: string;
  title: string;
  colour: string;
  /** the area's own percentile in this category, 0-100, or null for no answer */
  score: number | null;
  /** one published figure, already worded; empty while it is still loading */
  detail: string;
  /** whether this category carries weight, for the reader who turned it off */
  weighted: boolean;
  /** this category is the one being viewed on its own */
  viewing: boolean;
  /** the published indices behind `detail`, for the `i` beside it */
  notes: SourceNote[];
  /** whether this row's `i` is open */
  info: boolean;
}

export interface PanelArgs {
  name: string;
  /** the blend at this place under the current weights, 0-100, or null */
  blended: number | null;
  /** why there is no blended score, when there is something worth saying */
  blockedBy: string | null;
  rows: PanelRow[];
  detail: boolean;
  /** the figures have not arrived yet, so the detail lines are still blank */
  pending: boolean;
}

function noteHtml(note: SourceNote): string {
  return `
    <div class="note">
      <div class="note-title">${escapeHtml(note.title)} · ${escapeHtml(note.publisher)}</div>
      <p class="note-what">${escapeHtml(note.what)}</p>
      <a class="note-link" href="${escapeHtml(note.url)}" target="_blank" rel="noopener">
        ${escapeHtml(linkHost(note.url))} →</a>
    </div>`;
}

/**
 * The detail line, and the `i` that explains where its figures came from.
 *
 * The `i` is a control inside a control -- the row around it selects a layer --
 * so it sits outside the row's own button rather than nested in it, which no
 * browser would accept and no screen reader could announce.
 */
function detailHtml(row: PanelRow): string {
  if (!row.detail) return "";
  const info = row.notes.length
    ? `<button class="info${row.info ? " is-open" : ""}" data-info="${row.name}"
               aria-expanded="${row.info}"
               aria-label="Where the ${escapeHtml(row.title)} figures come from">i</button>`
    : "";
  return `
    <div class="row-detail">
      <div class="detail-line"><span class="detail-text">${row.detail}</span>${info}</div>
      ${row.info ? row.notes.map(noteHtml).join("") : ""}
    </div>`;
}

function bar(row: PanelRow): string {
  const width = row.score === null ? 0 : Math.max(row.score, 1.5);
  const dim = row.weighted ? "" : " is-off";
  const on = row.viewing ? " is-view" : "";
  return `
    <button class="row${dim}${on}" data-select="${row.name}" aria-pressed="${row.viewing}">
      <span class="dot" style="background:${row.colour}"></span>
      <span class="row-name">${escapeHtml(row.title)}</span>
      <span class="track"><span class="fill" style="width:${width}%;background:${row.colour}">
      </span></span>
      <span class="row-value">${percent(row.score)}</span>
    </button>
    ${detailHtml(row)}`;
}

export function renderAreaPanel(args: PanelArgs): string {
  const score = args.blended === null
    ? (args.blockedBy
        ? `<div class="blend-none">${escapeHtml(args.blockedBy)}</div>`
        : "")
    : `<div class="blend"><b>${args.blended}</b><span>out of 100 based on your selection</span></div>`;

  const rows = args.rows.map((row) => bar(row)).join("");
  const detail = args.detail ? " is-open" : "";

  return `
    <div class="panel-head">
      <div class="panel-name">${escapeHtml(args.name || "This area")}</div>
      <button class="close" data-action="close-area" aria-label="Close">×</button>
    </div>
    ${score}
    <div class="rows${detail}">${rows}</div>
    <button class="more" data-action="toggle-detail">
      ${args.detail ? "Less detail ▲" : "More detail ▼"}
    </button>
    ${args.detail && args.pending
      ? `<p class="panel-note">Fetching the published figures…</p>`
      : ""}`;
}
